import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Building2,
  Hash,
  Loader2,
  Boxes,
  Wrench,
  ListChecks,
  AlertOctagon,
  Plus,
  Users,
} from "lucide-react";
import Pill from "@/components/Pill";
import AddInlineDialog from "@/components/AddInlineDialog";
import AddContactDialog from "@/components/AddContactDialog";
import { useRefData } from "@/context/RefDataContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRefData();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [related, setRelated] = useState({ assets: [], services: [], tasks: [], issues: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // "task" | "issue" | "asset" | "service" | "contact" | null

  const refreshRelated = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}/related`);
      setRelated(data);
    } catch (_) {
      // ignore
    }
  }, [id]);

  const refreshDoc = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setDoc(data);
    } catch (_) {
      // ignore
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/projects/${id}/related`),
        ]);
        setDoc(a.data);
        setRelated(b.data);
      } catch (e) {
        toast.error("CDARK not found");
        navigate("/projects");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="project-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading CDARK…
      </div>
    );
  }
  if (!doc) return null;

  const contacts = Array.isArray(doc.contact_persons) ? doc.contact_persons : [];

  // -------- Add-dialog configs --------
  const taskCfg = {
    endpoint: "/tasks",
    title: "New Task",
    fields: [
      { key: "subject", label: "Subject", required: true, full: true },
      { key: "project_id", label: "CDARK", type: "select", optionsRef: "projects" },
      { key: "team_id", label: "Team", type: "select", optionsRef: "teams" },
      { key: "priority", label: "Priority", type: "select", optionsRef: "priorities" },
      { key: "status", label: "Status", type: "select", optionsRef: "statuses" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "details", label: "Details", type: "textarea", full: true },
    ],
    initialForm: { subject: "", project_id: id, team_id: "", priority: "Medium", status: "Open", due_date: "", details: "" },
    locked: ["project_id"],
  };

  const issueCfg = {
    endpoint: "/issues",
    title: "New Ticket",
    fields: [
      { key: "subject", label: "Subject", required: true, full: true },
      { key: "project_id", label: "CDARK", type: "select", optionsRef: "projects" },
      { key: "asset_id", label: "Related Asset (optional)", type: "select", optionsRef: "assets", filterBy: "project_id" },
      { key: "service_id", label: "Related Service (optional)", type: "select", optionsRef: "services", filterBy: "project_id" },
      { key: "team_id", label: "Team", type: "select", optionsRef: "teams" },
      { key: "priority", label: "Priority", type: "select", optionsRef: "priorities" },
      { key: "status", label: "Status", type: "select", optionsRef: "statuses" },
      { key: "description", label: "Description", type: "textarea", full: true },
    ],
    initialForm: { subject: "", project_id: id, asset_id: "", service_id: "", team_id: "", priority: "Medium", status: "Open", description: "" },
    locked: ["project_id"],
  };

  const assetCfg = {
    endpoint: "/assets",
    title: "New Asset",
    fields: [
      { key: "asset_type", label: "Asset Type", type: "select", optionsRef: "asset_types", required: true, full: true },
      { key: "project_id", label: "CDARK", type: "select", optionsRef: "projects" },
      { key: "oem_name", label: "OEM Name" },
      { key: "model_number", label: "Model #" },
    ],
    initialForm: { asset_type: "", project_id: id, oem_name: "", model_number: "" },
    locked: ["project_id"],
  };

  const serviceCfg = {
    endpoint: "/services",
    title: "New Service",
    fields: [
      { key: "service_type", label: "Service Type", type: "select", optionsRef: "service_types", required: true },
      { key: "service_provider_name", label: "Provider", required: true, full: true },
      { key: "project_id", label: "CDARK", type: "select", optionsRef: "projects" },
      { key: "service_provider_support_number", label: "Support #" },
    ],
    initialForm: { service_type: "", service_provider_name: "", project_id: id, service_provider_support_number: "" },
    locked: ["project_id"],
  };

  const active = {
    task: taskCfg,
    issue: issueCfg,
    asset: assetCfg,
    service: serviceCfg,
  }[adding];

  return (
    <div className="space-y-8" data-testid="project-detail-page">
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="project-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to CDARK
        </Link>
      </div>

      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            <span className="font-mono normal-case tracking-normal" data-testid="project-detail-code">
              {doc.code}
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }} data-testid="project-detail-name">
            {doc.client_name || doc.name}
          </h1>
          {(doc.client_relationship_number || doc.address || doc.city || doc.state || doc.pin_code || doc.google_map_link) && (
            <p
              className="text-sm text-zinc-400 mt-2 flex items-center gap-x-3 gap-y-1.5 flex-wrap"
              data-testid="project-detail-meta"
            >
              {doc.client_relationship_number && (
                <span className="text-zinc-500 font-mono text-xs">
                  Rel #: {doc.client_relationship_number}
                </span>
              )}
              {(doc.address || doc.city || doc.state || doc.pin_code) && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span>
                    {[doc.address, doc.city, doc.state, doc.pin_code]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </span>
              )}
              {doc.google_map_link && (
                <a
                  href={doc.google_map_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline"
                  data-testid="detail-map-link"
                >
                  Map <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </p>
          )}
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate("/projects")}
          className="bg-zinc-900 border border-white/10 text-white hover:bg-zinc-800"
          data-testid="project-detail-back-list"
        >
          Back to list
        </Button>
      </header>

      {/* Row 1: Tasks + Issues */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RelatedList
          title="Linked tasks"
          icon={ListChecks}
          items={related.tasks}
          link="/tasks"
          onAdd={() => setAdding("task")}
          canAdd={can(user, "tasks", "create")}
          addTestid="add-task-btn"
          render={(t) => (
            <>
              <div className="text-sm text-white truncate">{t.subject}</div>
              <div className="text-xs text-zinc-500 font-mono">{t.code}</div>
            </>
          )}
          right={(t) => (
            <div className="flex gap-1.5 items-center">
              <Pill kind="priority" value={t.priority} />
              <Pill kind="status" value={t.status} />
            </div>
          )}
          testid="related-tasks"
        />
        <RelatedList
          title="Linked tickets"
          icon={AlertOctagon}
          items={related.issues}
          link="/issues"
          onAdd={() => setAdding("issue")}
          canAdd={can(user, "issues", "create")}
          addTestid="add-issue-btn"
          render={(i) => (
            <>
              <div className="text-sm text-white truncate">{i.subject}</div>
              <div className="text-xs text-zinc-500 font-mono">{i.code}</div>
            </>
          )}
          right={(i) => (
            <div className="flex gap-1.5 items-center">
              <Pill kind="priority" value={i.priority} />
              <Pill kind="status" value={i.status} />
            </div>
          )}
          testid="related-issues"
        />
      </section>

      {/* Row 2: Assets + Services */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RelatedList
          title="Linked assets"
          icon={Boxes}
          items={related.assets}
          link="/assets"
          onAdd={() => setAdding("asset")}
          canAdd={can(user, "assets", "create")}
          addTestid="add-asset-btn"
          render={(a) => (
            <>
              <div className="text-sm text-white truncate">{a.asset_type || "—"}</div>
              <div className="text-xs text-zinc-500 font-mono">{a.code}</div>
            </>
          )}
          right={(a) => (
            <span className="text-xs text-zinc-500 truncate max-w-[120px]">{a.oem_name || ""}</span>
          )}
          testid="related-assets"
        />
        <RelatedList
          title="Linked services"
          icon={Wrench}
          items={related.services}
          link="/services"
          onAdd={() => setAdding("service")}
          canAdd={can(user, "services", "create")}
          addTestid="add-service-btn"
          render={(s) => (
            <>
              <div className="text-sm text-white truncate">{s.service_type}</div>
              <div className="text-xs text-zinc-500 font-mono">{s.code} · {s.service_provider_name || "—"}</div>
            </>
          )}
          right={(s) => (
            <span className="text-xs text-zinc-500 truncate max-w-[120px]">
              {s.service_provider_name || ""}
            </span>
          )}
          testid="related-services"
        />
      </section>

      {/* Row 3: Contact persons */}
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid="detail-contacts">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 label-overline">
            <Users className="w-3.5 h-3.5" strokeWidth={1.75} /> Contact persons
          </div>
          <button
            type="button"
            onClick={() => setAdding("contact")}
            className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1 transition-colors"
            data-testid="add-contact-btn"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {contacts.length === 0 ? (
          <div className="text-xs text-zinc-600 py-6 text-center">No contact persons added.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map((c, idx) => (
              <div
                key={idx}
                className="bg-zinc-950 border border-white/10 rounded-lg p-4 hover:-translate-y-0.5 transition-transform"
                data-testid={`detail-contact-${idx}`}
              >
                <div className="text-sm font-medium text-white">{c.name || "Unnamed"}</div>
                <div className="mt-3 space-y-1.5">
                  {c.email && (
                    <a
                      href={`mailto:${c.email}`}
                      className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      <span className="truncate">{c.email}</span>
                    </a>
                  )}
                  {c.phone && (
                    <a
                      href={`tel:${c.phone}`}
                      className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>{c.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="text-xs text-zinc-600 pt-4 border-t border-white/5">
        Created {doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"}
      </footer>

      {/* Add dialogs */}
      {active && (
        <AddInlineDialog
          open={!!adding && adding !== "contact"}
          onOpenChange={(o) => !o && setAdding(null)}
          endpoint={active.endpoint}
          title={active.title}
          fields={active.fields}
          initialForm={active.initialForm}
          lockedKeys={active.locked}
          onCreated={() => {
            setAdding(null);
            refreshRelated();
            ref.refresh?.();
          }}
        />
      )}
      <AddContactDialog
        open={adding === "contact"}
        onOpenChange={(o) => !o && setAdding(null)}
        projectId={id}
        currentContacts={contacts}
        onAdded={() => {
          setAdding(null);
          refreshDoc();
        }}
      />
    </div>
  );
}

function RelatedList({ title, icon: Icon, items, render, right, link, onAdd, canAdd = true, addTestid, testid }) {
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 label-overline">
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {title}
        </div>
        <div className="flex items-center gap-3">
          <Link to={link} className="text-xs text-zinc-500 hover:text-white">
            View all →
          </Link>
          {canAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1 transition-colors"
              data-testid={addTestid}
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          )}
        </div>
      </div>
      {!items || items.length === 0 ? (
        <div className="text-xs text-zinc-600 py-6 text-center">Nothing linked yet.</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.slice(0, 6).map((it) => (
            <li
              key={it.id}
              className="py-2.5 flex items-center justify-between gap-3"
              data-testid={`${testid}-${it.id}`}
            >
              <div className="min-w-0">{render(it)}</div>
              <div className="shrink-0">{right(it)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
